<template>
  <page-template :is-loading="isLoading" :data-exists="edgeFunctions !== null">
    <template #pageHeadline>
      {{ $t('edgeFunction.listPage.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="edgeFunctions"
        :sticky-header="true"
        class="rbz-list-table"
        data-qa="edge-functions-table"
        :row-buttons="rowButtons"
        :new-button-link="getNewEdgeFunctionPath()"
        :show-filter-row="true"
        @duplicate-row-button-clicked="duplicateEdgeFunction"
        @delete-row-button-clicked="openDeleteEdgeFunctionModal"
        @download-data="downloadDoc"
      >
      </rbz-table>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="loadEdgeFunctions"
      />
      <rbz-modal-dialog
        :show="showDeleteEdgeFunctionModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteEdgeFunction"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteEdgeFunctionModal = false"
        @closeModal="showDeleteEdgeFunctionModal = false"
        size="medium"
      >
        <template #content>
          {{ $t('common.operations.delete.modal.message', { name: selectedEdgeFunction.name }) }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { DocumentName, EdgeFunction } from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import PageTemplate from '@/views/PageTemplate.vue';
import _ from 'lodash';
import GitHistory from '@/components/GitHistory.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import { useRouting } from '@/helpers/RoutingUtils';
import { useUserStore } from '@/store/UserStore';

export default defineComponent({
  name: 'EdgeFunctionsList',
  setup() {
    const { getEditPath, getNewPath, navigateTo } = useRouting();
    return {
      getEditPath,
      getNewPath,
      navigateTo,
    };
  },
  components: {
    GitHistory,
    PageTemplate,
    RbzModalDialog,
    RbzTable,
  },
  data() {
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('edgeFunction.listPage.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'clickable',
          autoFocus: true,
          linkCallback: (item) => {
            return this.getEdgeFunctionPath(item);
          },
        },
        {
          id: 'phase',
          title: this.$t('edgeFunction.phase'),
          fieldNames: ['phase'],
          displayFunction: (item: EdgeFunction) => {
            return this.$t(
              `edgeFunction.phaseValue.${Utils.snakeCaseToCamelCase(item.phase.toLowerCase())}`
            );
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-200px',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          linkCallback: this.getEdgeFunctionPath,
        },
        {
          id: 'duplicate',
          tooltip: this.$t('common.operations.duplicate.tableTooltip'),
          icon: 'IconDuplicate',
        },
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: this.isDeleteDisabled,
          tooltipCallback: this.deleteTooltip,
        },
      ] as RbzTableRowButton[],
      edgeFunctions: null as EdgeFunction[],
      pageLoadingCounter: null,

      showDeleteEdgeFunctionModal: false,
      selectedEdgeFunction: null,

      referencedIds: [],
    };
  },

  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if ((this.$route.name as string).includes('Edge Functions List') && val && val !== oldVal) {
          this.setPageLoadingStatus(true);
          await this.loadEdgeFunctions();
          await this.loadReferencedIds();
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
  },

  computed: {
    selectedBranch(): string {
      if (!this.branchesStore.selectedBranchId) {
        this.branchesStore.setSelectedBranch(this.branchesStore.defaultBranch);
      }
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('edgeFunction.plural'),
        singular: this.$t('edgeFunction.singular'),
      };
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/edge-functions/versions`;
    },
  },

  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    async loadEdgeFunctions() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/edge-functions`;
      const fieldNames = _.flatMap(this.columns, 'fieldNames');
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url,
        config: { headers: { 'x-fields': `id, ${_.uniq(fieldNames).join(', ')}` } },
      });
      this.edgeFunctions = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    async loadReferencedIds() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/security-policies`,
      });
      const securityPolicies = response?.data?.items || [];
      const referencedIds: string[] = [];

      _.forEach(securityPolicies, (securityPolicy) => {
        _.forEach(securityPolicy.map, (location) => {
          referencedIds.push(location['edge_functions']);
        });
      });
      this.referencedIds = _.uniq(_.flatten(referencedIds));
      this.setPageLoadingStatus(false);
    },

    isDeleteDisabled(edgeFunction: EdgeFunction) {
      return (
        this.isEdgeFunctionProtected(edgeFunction) || this.isEdgeFunctionReferenced(edgeFunction)
      );
    },

    isEdgeFunctionProtected(edgeFunction: EdgeFunction): boolean {
      return edgeFunction.id.startsWith('__');
    },

    isEdgeFunctionReferenced(edgeFunction: EdgeFunction): boolean {
      return this.referencedIds.includes(edgeFunction.id);
    },

    deleteTooltip(edgeFunction: EdgeFunction): string {
      if (this.isEdgeFunctionProtected(edgeFunction)) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      if (this.isEdgeFunctionReferenced(edgeFunction)) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return this.$t('common.operations.delete.tableTooltip');
    },

    getNewEdgeFunctionPath() {
      return this.getNewPath('edge-functions');
    },

    getEdgeFunctionPath(edgeFunction: EdgeFunction) {
      return this.getEditPath('edge-functions', edgeFunction.id);
    },

    editEdgeFunction(edgeFunction: EdgeFunction) {
      this.navigateTo(this.getEdgeFunctionPath(edgeFunction));
    },

    async duplicateEdgeFunction(edgeFunction: EdgeFunction) {
      this.setPageLoadingStatus(true);
      const getOriginalResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/edge-functions/${edgeFunction.id}`,
      });
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      if (!getOriginalResponse?.data) {
        Utils.toast({
          message: failureMessage,
          type: 'is-danger',
        });
        return;
      }
      const originalEdgeFunction = getOriginalResponse.data;
      const edgeFunctionToAdd = _.cloneDeep(originalEdgeFunction);
      edgeFunctionToAdd.id = DatasetsUtils.generateUUID2();
      edgeFunctionToAdd.name = Utils.generateUniqueEntityName(
        edgeFunctionToAdd.name,
        _.map(this.edgeFunctions, 'name'),
        true
      );
      const url = `conf/${this.selectedBranch}/edge-functions/${edgeFunctionToAdd.id}`;
      const postNewResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: edgeFunctionToAdd,
        successMessage,
        failureMessage,
      });
      if (postNewResponse) {
        this.editEdgeFunction(edgeFunctionToAdd);
      }
      this.setPageLoadingStatus(false);
    },

    async deleteEdgeFunction() {
      this.setPageLoadingStatus(true);
      const edgeFunctionId = this.selectedEdgeFunction.id;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/edge-functions/${edgeFunctionId}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.edgeFunctions = _.filter(this.edgeFunctions, (edgeFunction) => {
          return edgeFunction.id !== edgeFunctionId;
        });
      }
      this.showDeleteEdgeFunctionModal = false;
      this.setPageLoadingStatus(false);
    },

    downloadDoc() {
      Utils.downloadFile('edge-functions', 'json', this.edgeFunctions);
    },

    openDeleteEdgeFunctionModal(edgeFunction: EdgeFunction) {
      this.showDeleteEdgeFunctionModal = true;
      this.selectedEdgeFunction = edgeFunction;
    },
  },
});
</script>
